<script setup>
// Workspace → Drive v3 (pedido Ivan 26/08: "igual do Google"): sidebar
// interna (Novo, Meu Drive, Recentes, Com estrela, Lixeira, Armazenamento
// com "comprar mais"), pastas + breadcrumb, grade com miniaturas/lista,
// renomear/mover/estrela, lixeira com restaurar, preview e drag-and-drop.
// Storage é o da assinatura — upload barrado quando o espaço acaba (402).
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { driveFilesAPI, driveFoldersAPI } from 'dashboard/api/workspace';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const isLoading = ref(true);
const isUploading = ref(false);
const section = ref('drive'); // drive | recent | starred | trash
const folders = ref([]);
const files = ref([]);
const allFolders = ref([]);
const breadcrumb = ref([]);
const currentFolderId = ref(null);
const usage = ref({ current_mb: 0, limit_mb: null });
const search = ref('');
const viewMode = ref('grid');
const fileInput = ref(null);
const showNewMenu = ref(false);
const menuFor = ref(null);
const preview = ref(null);
const moveTarget = ref(null);
const moveFolderTarget = ref(null); // pasta sendo movida
const shareTarget = ref(null); // arquivo no modal de compartilhamento
const isSharing = ref(false);
const shareCopied = ref(false);
const sortBy = ref('date'); // date | name | size
const showSortMenu = ref(false);
const nameModal = ref(null);

const SECTIONS = [
  { key: 'drive', icon: 'i-lucide-hard-drive', label: 'ROOT' },
  { key: 'recent', icon: 'i-lucide-clock', label: 'RECENT' },
  { key: 'starred', icon: 'i-lucide-star', label: 'STARRED' },
  { key: 'trash', icon: 'i-lucide-trash-2', label: 'TRASH' },
];

const fetchContent = async () => {
  isLoading.value = true;
  try {
    const scope = section.value === 'drive' ? 'folder' : section.value;
    const { data } = await driveFilesAPI.list({
      folderId: section.value === 'drive' ? currentFolderId.value : null,
      scope,
    });
    folders.value = data.folders || [];
    files.value = data.files || [];
    allFolders.value = data.all_folders || [];
    breadcrumb.value = data.breadcrumb || [];
    usage.value = data.usage || usage.value;
  } catch {
    useAlert(t('WORKSPACE_DRIVE.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openSection = key => {
  section.value = key;
  currentFolderId.value = null;
  search.value = '';
  fetchContent();
};

const openFolder = id => {
  section.value = 'drive';
  currentFolderId.value = id;
  search.value = '';
  fetchContent();
};

const filteredFolders = computed(() => {
  const termo = search.value.trim().toLowerCase();
  if (!termo) return folders.value;
  return folders.value.filter(f => f.name.toLowerCase().includes(termo));
});

const filteredFiles = computed(() => {
  const termo = search.value.trim().toLowerCase();
  const lista = termo
    ? files.value.filter(f => f.name.toLowerCase().includes(termo))
    : [...files.value];
  if (sortBy.value === 'name')
    return lista.sort((x, y) => x.name.localeCompare(y.name));
  if (sortBy.value === 'size')
    return lista.sort((x, y) => (y.byte_size || 0) - (x.byte_size || 0));
  return lista.sort((x, y) => new Date(y.created_at) - new Date(x.created_at));
});

const usagePercent = computed(() => {
  if (!usage.value.limit_mb) return 0;
  return Math.min(100, (usage.value.current_mb / usage.value.limit_mb) * 100);
});

const formatSize = bytes => {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatMb = mb => {
  if (mb == null) return '∞';
  if (mb < 1024) return `${mb.toFixed(0)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
};

const formatDate = value =>
  value
    ? new Date(value).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      })
    : '';

const iconFor = type => {
  if (!type) return 'i-lucide-file';
  if (type.startsWith('image/')) return 'i-lucide-image';
  if (type.startsWith('video/')) return 'i-lucide-video';
  if (type.startsWith('audio/')) return 'i-lucide-music';
  if (type.includes('pdf')) return 'i-lucide-file-text';
  if (type.includes('sheet') || type.includes('csv') || type.includes('excel'))
    return 'i-lucide-table';
  if (type.includes('zip') || type.includes('rar')) return 'i-lucide-archive';
  return 'i-lucide-file';
};

const previewKind = file => {
  const type = file?.content_type || '';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  if (type.includes('pdf')) return 'pdf';
  return 'none';
};

// ===== Upload =====
const uploadFiles = async fileList => {
  const lista = Array.from(fileList || []);
  if (!lista.length) return;
  isUploading.value = true;
  showNewMenu.value = false;
  try {
    // Sequencial de propósito: cada upload revalida o limite no servidor.
    // eslint-disable-next-line no-restricted-syntax
    for (const file of lista) {
      // eslint-disable-next-line no-await-in-loop
      await driveFilesAPI.upload(file, currentFolderId.value);
    }
    useAlert(t('WORKSPACE_DRIVE.UPLOAD_SUCCESS', { count: lista.length }));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WORKSPACE_DRIVE.UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
    await fetchContent();
  }
};

const onPick = event => {
  uploadFiles(event.target.files);
  event.target.value = '';
};

const onDrop = event => {
  if (section.value !== 'trash') uploadFiles(event.dataTransfer?.files);
};

// ===== Modal de nome =====
const openNameModal = (kind, id = null, value = '') => {
  showNewMenu.value = false;
  menuFor.value = null;
  nameModal.value = { kind, id, value };
};

const confirmNameModal = async () => {
  const modal = nameModal.value;
  const nome = modal?.value?.trim();
  if (!nome) return;
  try {
    if (modal.kind === 'new_folder') {
      await driveFoldersAPI.create({
        name: nome,
        parent_id: currentFolderId.value,
      });
    } else if (modal.kind === 'rename_folder') {
      await driveFoldersAPI.update(modal.id, { name: nome });
    } else {
      await driveFilesAPI.update(modal.id, { name: nome });
    }
    nameModal.value = null;
    await fetchContent();
  } catch {
    useAlert(t('WORKSPACE_DRIVE.SAVE_ERROR'));
  }
};

// ===== Ações de arquivo =====
const toggleStar = async file => {
  menuFor.value = null;
  try {
    await driveFilesAPI.update(file.id, { starred: !file.starred });
    file.starred = !file.starred;
    if (section.value === 'starred') await fetchContent();
  } catch {
    useAlert(t('WORKSPACE_DRIVE.SAVE_ERROR'));
  }
};

// ===== Compartilhamento por link (27/08): arquivo E pasta =====
const shareKind = ref('file'); // file | folder

const openShare = (item, kind = 'file') => {
  menuFor.value = null;
  shareCopied.value = false;
  shareKind.value = kind;
  shareTarget.value = item;
};

const toggleShare = async () => {
  const item = shareTarget.value;
  if (!item || isSharing.value) return;
  isSharing.value = true;
  try {
    const api = shareKind.value === 'folder' ? driveFoldersAPI : driveFilesAPI;
    const { data } = await api.update(item.id, { shared: !item.shared });
    item.shared = data.shared;
    item.share_url = data.share_url;
    const lista = shareKind.value === 'folder' ? folders.value : files.value;
    const original = lista.find(f => f.id === item.id);
    if (original) Object.assign(original, data);
  } catch {
    useAlert(t('WORKSPACE_DRIVE.SAVE_ERROR'));
  } finally {
    isSharing.value = false;
  }
};

// "Fazer uma cópia" (estilo Google)
const copyFile = async file => {
  menuFor.value = null;
  try {
    await driveFilesAPI.copy(file.id);
    await fetchContent();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WORKSPACE_DRIVE.SAVE_ERROR'));
  }
};

// Link PRIVADO (27/08, pedido Ivan: escolher público OU privado): URL do
// próprio app — só quem está logado na conta abre.
const privateShareLink = computed(() => {
  if (!shareTarget.value) return '';
  const chave = shareKind.value === 'folder' ? 'pasta' : 'arquivo';
  return `${window.location.origin}/app/accounts/${route.params.accountId}/workspace/drive?${chave}=${shareTarget.value.id}`;
});

const currentShareLink = computed(() =>
  shareTarget.value?.shared
    ? shareTarget.value.share_url
    : privateShareLink.value
);

const selectShareMode = mode => {
  const publico = mode === 'public';
  if (publico !== !!shareTarget.value?.shared) toggleShare();
};

const copyShareLink = async () => {
  if (!currentShareLink.value) return;
  await navigator.clipboard.writeText(currentShareLink.value);
  shareCopied.value = true;
  window.setTimeout(() => {
    shareCopied.value = false;
  }, 2000);
};

// ===== Mover pasta =====
const moveFolder = async parentId => {
  const alvo = moveFolderTarget.value;
  if (!alvo) return;
  try {
    await driveFoldersAPI.update(alvo.id, { parent_id: parentId });
    moveFolderTarget.value = null;
    await fetchContent();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WORKSPACE_DRIVE.SAVE_ERROR'));
  }
};

const moveFile = async folderId => {
  try {
    await driveFilesAPI.update(moveTarget.value.id, { folder_id: folderId });
    moveTarget.value = null;
    await fetchContent();
  } catch {
    useAlert(t('WORKSPACE_DRIVE.SAVE_ERROR'));
  }
};

// Excluir manda pra LIXEIRA (sem confirm, estilo Google); na lixeira o
// excluir é permanente (com confirm).
const trashFile = async file => {
  menuFor.value = null;
  try {
    await driveFilesAPI.delete(file.id);
    files.value = files.value.filter(f => f.id !== file.id);
    useAlert(t('WORKSPACE_DRIVE.TRASHED', { name: file.name }));
  } catch {
    useAlert(t('WORKSPACE_DRIVE.DELETE_ERROR'));
  }
};

const restoreFile = async file => {
  menuFor.value = null;
  try {
    await driveFilesAPI.update(file.id, { restore: true });
    files.value = files.value.filter(f => f.id !== file.id);
  } catch {
    useAlert(t('WORKSPACE_DRIVE.SAVE_ERROR'));
  }
};

const deleteForever = async file => {
  menuFor.value = null;
  // eslint-disable-next-line no-alert
  const ok = window.confirm(
    t('WORKSPACE_DRIVE.DELETE_FOREVER_CONFIRM', { name: file.name })
  );
  if (!ok) return;
  try {
    await driveFilesAPI.deletePermanent(file.id);
    files.value = files.value.filter(f => f.id !== file.id);
  } catch {
    useAlert(t('WORKSPACE_DRIVE.DELETE_ERROR'));
  }
};

const deleteFolder = async folder => {
  menuFor.value = null;
  // eslint-disable-next-line no-alert
  const ok = window.confirm(
    t('WORKSPACE_DRIVE.FOLDER_DELETE_CONFIRM', { name: folder.name })
  );
  if (!ok) return;
  try {
    await driveFoldersAPI.delete(folder.id);
    await fetchContent();
  } catch {
    useAlert(t('WORKSPACE_DRIVE.DELETE_ERROR'));
  }
};

const goToBilling = () => {
  router.push({ name: 'billing_settings_index' }).catch(() => {});
};

// Menu do ARQUIVO é teleportado (position:fixed no body) — dentro do card
// qualquer ancestral com overflow/stacking cortava ele.
const menuPos = ref({ x: 0, y: 0 });

const toggleMenu = (kind, id, event) => {
  const aberto = menuFor.value?.kind === kind && menuFor.value?.id === id;
  if (aberto) {
    menuFor.value = null;
    return;
  }
  if (event?.currentTarget) {
    const rect = event.currentTarget.getBoundingClientRect();
    menuPos.value = {
      x: Math.max(8, Math.min(rect.right - 192, window.innerWidth - 200)),
      y: Math.min(rect.bottom + 4, window.innerHeight - 320),
    };
  }
  menuFor.value = { kind, id };
};

const menuFile = computed(() =>
  menuFor.value?.kind === 'file'
    ? files.value.find(f => f.id === menuFor.value.id)
    : null
);

// Navegação do preview (estilo Google Drive)
const previewIndex = computed(() =>
  filteredFiles.value.findIndex(f => f.id === preview.value?.id)
);
const navPreview = delta => {
  const lista = filteredFiles.value;
  if (!lista.length) return;
  const idx = (previewIndex.value + delta + lista.length) % lista.length;
  preview.value = lista[idx];
};

const closeMenus = () => {
  menuFor.value = null;
  showNewMenu.value = false;
  showSortMenu.value = false;
};

onMounted(async () => {
  if (route.query.pasta) {
    currentFolderId.value = Number(route.query.pasta);
  }
  await fetchContent();
  if (route.query.arquivo) {
    try {
      const { data } = await driveFilesAPI.show(route.query.arquivo);
      preview.value = data;
    } catch {
      useAlert(t('WORKSPACE_DRIVE.LOAD_ERROR'));
    }
  }
  document.addEventListener('click', closeMenus);
});
onBeforeUnmount(() => document.removeEventListener('click', closeMenus));
</script>

<template>
  <div
    class="flex flex-1 overflow-hidden bg-n-background"
    @dragover.prevent
    @drop.prevent="onDrop"
  >
    <!-- Sidebar interna (estilo Google Drive) -->
    <aside
      class="w-56 shrink-0 border-r border-n-weak p-3 flex flex-col gap-4 overflow-y-auto"
    >
      <div class="relative" @click.stop>
        <button
          class="flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-2xl bg-n-solid-2 border border-n-weak text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-60 transition-colors shadow-sm"
          :disabled="isUploading"
          @click="showNewMenu = !showNewMenu"
        >
          <span
            :class="
              isUploading
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-plus'
            "
            class="w-5 h-5"
          />
          {{ t('WORKSPACE_DRIVE.NEW') }}
        </button>
        <div
          v-if="showNewMenu"
          class="absolute left-0 mt-1 w-52 rounded-xl border border-n-weak bg-n-solid-1 shadow-lg z-20 py-1"
        >
          <button
            class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
            @click="openNameModal('new_folder')"
          >
            <span class="i-lucide-folder-plus w-4 h-4 text-n-slate-10" />
            {{ t('WORKSPACE_DRIVE.NEW_FOLDER') }}
          </button>
          <button
            class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
            @click="fileInput?.click()"
          >
            <span class="i-lucide-upload w-4 h-4 text-n-slate-10" />
            {{ t('WORKSPACE_DRIVE.UPLOAD') }}
          </button>
        </div>
      </div>
      <input
        ref="fileInput"
        type="file"
        multiple
        class="hidden"
        @change="onPick"
      />

      <nav class="space-y-0.5">
        <button
          v-for="item in SECTIONS"
          :key="item.key"
          class="flex items-center gap-3 w-full px-3 py-2 text-sm rounded-full transition-colors"
          :class="
            section === item.key
              ? 'bg-n-brand/15 text-n-brand font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="openSection(item.key)"
        >
          <span :class="item.icon" class="w-4 h-4" />
          {{ t(`WORKSPACE_DRIVE.${item.label}`) }}
        </button>
      </nav>

      <!-- Armazenamento -->
      <div class="mt-auto space-y-2 px-1">
        <div class="flex items-center gap-2 text-xs text-n-slate-10">
          <span class="i-lucide-cloud w-3.5 h-3.5" />
          {{ t('WORKSPACE_DRIVE.USAGE') }}
        </div>
        <div class="h-1.5 rounded-full bg-n-alpha-2 overflow-hidden">
          <div
            class="h-full rounded-full transition-all"
            :class="usagePercent > 90 ? 'bg-n-ruby-9' : 'bg-n-brand'"
            :style="{ width: `${usagePercent}%` }"
          />
        </div>
        <p class="text-xs text-n-slate-10 tabular-nums">
          {{
            t('WORKSPACE_DRIVE.USAGE_OF', {
              used: formatMb(usage.current_mb),
              total: formatMb(usage.limit_mb),
            })
          }}
        </p>
        <button
          class="w-full px-3 py-1.5 text-xs font-medium rounded-full border border-n-weak text-n-brand hover:bg-n-alpha-2 transition-colors"
          @click="goToBilling"
        >
          {{ t('WORKSPACE_DRIVE.BUY_MORE') }}
        </button>
      </div>
    </aside>

    <!-- Conteúdo -->
    <div class="flex-1 overflow-y-auto">
      <div class="px-6 py-5 max-w-6xl w-full mx-auto space-y-4">
        <!-- Topo: breadcrumb/título + busca + view -->
        <div class="flex flex-wrap items-center justify-between gap-3">
          <nav
            v-if="section === 'drive'"
            class="flex items-center gap-1 text-sm min-w-0"
          >
            <button
              class="px-2 py-1 rounded-lg font-semibold text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="openFolder(null)"
            >
              {{ t('WORKSPACE_DRIVE.ROOT') }}
            </button>
            <template v-for="crumb in breadcrumb" :key="crumb.id">
              <span class="i-lucide-chevron-right w-4 h-4 text-n-slate-9" />
              <button
                class="px-2 py-1 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 truncate max-w-[160px] transition-colors"
                :class="{
                  'font-semibold text-n-slate-12': crumb.id === currentFolderId,
                }"
                @click="openFolder(crumb.id)"
              >
                {{ crumb.name }}
              </button>
            </template>
          </nav>
          <h2 v-else class="text-base font-semibold text-n-slate-12">
            {{
              t(
                `WORKSPACE_DRIVE.${SECTIONS.find(s => s.key === section)?.label}`
              )
            }}
          </h2>

          <div class="flex items-center gap-2">
            <input
              v-model="search"
              class="h-9 w-52 px-3 text-sm rounded-full bg-n-solid-2 border border-n-weak text-n-slate-12 outline-none focus:border-n-brand"
              :placeholder="t('WORKSPACE_DRIVE.SEARCH')"
            />
            <!-- Dropdown próprio: o <select> nativo tem altura imposta pelo
                 CSS global e nunca alinhava com a busca. -->
            <div class="relative" @click.stop>
              <button
                class="flex h-9 items-center gap-1.5 px-3 text-sm rounded-full bg-n-solid-2 border border-n-weak text-n-slate-11 hover:text-n-slate-12 transition-colors"
                @click="showSortMenu = !showSortMenu"
              >
                {{ t(`WORKSPACE_DRIVE.SORT_${sortBy.toUpperCase()}`) }}
                <span class="i-lucide-chevron-down w-3.5 h-3.5" />
              </button>
              <div
                v-if="showSortMenu"
                class="absolute right-0 top-full mt-1 w-44 rounded-xl border border-n-weak bg-n-solid-1 shadow-lg z-40 py-1"
              >
                <button
                  v-for="opcao in ['date', 'name', 'size']"
                  :key="opcao"
                  class="flex items-center justify-between w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
                  @click="
                    sortBy = opcao;
                    showSortMenu = false;
                  "
                >
                  {{ t(`WORKSPACE_DRIVE.SORT_${opcao.toUpperCase()}`) }}
                  <span
                    v-if="sortBy === opcao"
                    class="i-lucide-check w-4 h-4 text-n-brand"
                  />
                </button>
              </div>
            </div>
            <div
              class="flex h-9 items-center rounded-full border border-n-weak bg-n-solid-2 px-0.5"
            >
              <button
                class="p-1.5 rounded-full transition-colors"
                :class="
                  viewMode === 'grid'
                    ? 'bg-n-brand text-white'
                    : 'text-n-slate-10 hover:text-n-slate-12'
                "
                :title="t('WORKSPACE_DRIVE.VIEW_GRID')"
                @click="viewMode = 'grid'"
              >
                <span class="i-lucide-layout-grid w-4 h-4" />
              </button>
              <button
                class="p-1.5 rounded-full transition-colors"
                :class="
                  viewMode === 'list'
                    ? 'bg-n-brand text-white'
                    : 'text-n-slate-10 hover:text-n-slate-12'
                "
                :title="t('WORKSPACE_DRIVE.VIEW_LIST')"
                @click="viewMode = 'list'"
              >
                <span class="i-lucide-list w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div v-if="isLoading" class="flex justify-center py-12">
          <span
            class="i-lucide-loader-circle w-6 h-6 animate-spin text-n-slate-9"
          />
        </div>

        <template v-else>
          <!-- Pastas -->
          <div v-if="filteredFolders.length" class="space-y-2">
            <p
              class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
            >
              {{ t('WORKSPACE_DRIVE.FOLDERS') }}
            </p>
            <div class="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
              <div
                v-for="folder in filteredFolders"
                :key="`folder-${folder.id}`"
                class="group relative flex items-center gap-2 px-3 py-2.5 rounded-xl bg-n-solid-2 border border-n-weak hover:border-n-brand/50 cursor-pointer transition-colors"
                @click="openFolder(folder.id)"
              >
                <span
                  class="i-lucide-folder w-5 h-5 text-n-amber-10 shrink-0"
                />
                <p class="flex-1 min-w-0 text-sm text-n-slate-12 truncate">
                  {{ folder.name }}
                </p>
                <span
                  v-if="folder.shared"
                  class="i-lucide-link w-3.5 h-3.5 text-n-teal-10 shrink-0"
                />
                <div class="relative" @click.stop>
                  <button
                    class="opacity-0 group-hover:opacity-100 p-1 rounded-lg text-n-slate-10 hover:bg-n-alpha-2 transition-all"
                    @click="toggleMenu('folder', folder.id)"
                  >
                    <span class="i-lucide-ellipsis-vertical w-4 h-4" />
                  </button>
                  <div
                    v-if="
                      menuFor?.kind === 'folder' && menuFor?.id === folder.id
                    "
                    class="absolute right-0 mt-1 w-44 rounded-xl border border-n-weak bg-n-solid-1 shadow-lg z-20 py-1"
                  >
                    <button
                      class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
                      @click="
                        openNameModal('rename_folder', folder.id, folder.name)
                      "
                    >
                      <span class="i-lucide-pencil w-4 h-4 text-n-slate-10" />
                      {{ t('WORKSPACE_DRIVE.RENAME') }}
                    </button>
                    <button
                      class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
                      @click="openShare(folder, 'folder')"
                    >
                      <span
                        class="i-lucide-link w-4 h-4"
                        :class="
                          folder.shared ? 'text-n-teal-10' : 'text-n-slate-10'
                        "
                      />
                      {{ t('WORKSPACE_DRIVE.SHARE') }}
                    </button>
                    <button
                      class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
                      @click="
                        moveFolderTarget = folder;
                        menuFor = null;
                      "
                    >
                      <span
                        class="i-lucide-folder-input w-4 h-4 text-n-slate-10"
                      />
                      {{ t('WORKSPACE_DRIVE.MOVE') }}
                    </button>
                    <button
                      class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-ruby-11 hover:bg-n-alpha-2"
                      @click="deleteFolder(folder)"
                    >
                      <span class="i-lucide-trash-2 w-4 h-4" />
                      {{ t('WORKSPACE_DRIVE.DELETE') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Vazio -->
          <div
            v-if="!filteredFolders.length && !filteredFiles.length"
            class="flex flex-col items-center gap-3 py-16 text-n-slate-10"
          >
            <span
              :class="
                section === 'trash' ? 'i-lucide-trash-2' : 'i-lucide-hard-drive'
              "
              class="w-10 h-10"
            />
            <p class="text-sm">
              {{
                section === 'trash'
                  ? t('WORKSPACE_DRIVE.TRASH_EMPTY')
                  : t('WORKSPACE_DRIVE.EMPTY')
              }}
            </p>
          </div>

          <!-- Arquivos -->
          <div v-if="filteredFiles.length" class="space-y-2">
            <p
              class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
            >
              {{ t('WORKSPACE_DRIVE.FILES') }}
            </p>

            <!-- GRADE -->
            <div
              v-if="viewMode === 'grid'"
              class="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
            >
              <div
                v-for="file in filteredFiles"
                :key="`file-${file.id}`"
                class="group relative rounded-xl bg-n-solid-2 border border-n-weak hover:border-n-brand/50 cursor-pointer transition-colors"
                :class="{
                  'z-30': menuFor?.kind === 'file' && menuFor?.id === file.id,
                }"
                @click="preview = file"
              >
                <!-- Anatomia estilo Google Drive (pedido Ivan 27/08):
                     cabeçalho (ícone+nome+menu) · miniatura grande · rodapé -->
                <div class="flex items-center gap-2 px-3 pt-3 pb-2">
                  <span
                    :class="iconFor(file.content_type)"
                    class="w-4 h-4 text-n-slate-10 shrink-0"
                  />
                  <p
                    class="flex-1 min-w-0 text-sm font-medium text-n-slate-12 truncate"
                    :title="file.name"
                  >
                    {{ file.name }}
                  </p>
                  <span
                    v-if="file.starred"
                    class="i-lucide-star w-3.5 h-3.5 text-n-amber-10 shrink-0"
                  />
                  <span
                    v-if="file.shared"
                    class="i-lucide-link w-3.5 h-3.5 text-n-teal-10 shrink-0"
                  />
                  <div class="relative" @click.stop>
                    <button
                      class="opacity-0 group-hover:opacity-100 p-1 rounded-lg text-n-slate-10 hover:bg-n-alpha-2 transition-all"
                      :class="{
                        'opacity-100':
                          menuFor?.kind === 'file' && menuFor?.id === file.id,
                      }"
                      @click="toggleMenu('file', file.id, $event)"
                    >
                      <span class="i-lucide-ellipsis-vertical w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div
                  class="mx-2 h-36 flex items-center justify-center bg-n-alpha-1 overflow-hidden rounded-lg"
                >
                  <img
                    v-if="file.thumbnail_url"
                    :src="file.thumbnail_url"
                    :alt="file.name"
                    class="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <video
                    v-else-if="
                      file.content_type?.startsWith('video/') && file.url
                    "
                    :src="file.url"
                    muted
                    preload="metadata"
                    class="w-full h-full object-cover pointer-events-none"
                  />
                  <span
                    v-else
                    :class="iconFor(file.content_type)"
                    class="w-10 h-10 text-n-slate-9"
                  />
                </div>
                <p class="px-3 py-2 text-[11px] text-n-slate-10 truncate">
                  {{
                    `${formatSize(file.byte_size)} · ${formatDate(file.created_at)}`
                  }}
                </p>
              </div>
            </div>

            <!-- LISTA -->
            <div v-else class="rounded-xl border border-n-weak overflow-hidden">
              <div
                v-for="file in filteredFiles"
                :key="`row-${file.id}`"
                class="group flex items-center gap-3 px-4 py-2.5 bg-n-solid-2 border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 cursor-pointer transition-colors"
                @click="preview = file"
              >
                <span
                  :class="iconFor(file.content_type)"
                  class="w-5 h-5 text-n-slate-10 shrink-0"
                />
                <p class="flex-1 min-w-0 text-sm text-n-slate-12 truncate">
                  {{ file.name }}
                </p>
                <span
                  v-if="file.starred"
                  class="i-lucide-star w-4 h-4 text-n-amber-10"
                />
                <span
                  class="text-xs text-n-slate-10 tabular-nums w-20 text-right"
                >
                  {{ formatSize(file.byte_size) }}
                </span>
                <span
                  class="text-xs text-n-slate-10 tabular-nums w-16 text-right"
                >
                  {{ formatDate(file.created_at) }}
                </span>
                <div class="flex items-center gap-1" @click.stop>
                  <template v-if="section !== 'trash'">
                    <button
                      class="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-n-slate-10 hover:text-n-amber-10 hover:bg-n-alpha-2 transition-all"
                      :title="t('WORKSPACE_DRIVE.STAR')"
                      @click="toggleStar(file)"
                    >
                      <span class="i-lucide-star w-4 h-4" />
                    </button>
                    <a
                      v-if="file.url"
                      :href="file.download_url || file.url"
                      download
                      class="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-all"
                      :title="t('WORKSPACE_DRIVE.DOWNLOAD')"
                    >
                      <span class="i-lucide-download w-4 h-4" />
                    </a>
                    <button
                      class="p-1.5 rounded-lg transition-all"
                      :class="
                        file.shared
                          ? 'text-n-teal-10'
                          : 'opacity-0 group-hover:opacity-100 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2'
                      "
                      :title="t('WORKSPACE_DRIVE.SHARE')"
                      @click="openShare(file)"
                    >
                      <span class="i-lucide-link w-4 h-4" />
                    </button>
                    <button
                      class="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-n-slate-10 hover:bg-n-alpha-2 transition-all"
                      :title="t('WORKSPACE_DRIVE.RENAME')"
                      @click="openNameModal('rename_file', file.id, file.name)"
                    >
                      <span class="i-lucide-pencil w-4 h-4" />
                    </button>
                    <button
                      class="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-n-slate-10 hover:bg-n-alpha-2 transition-all"
                      :title="t('WORKSPACE_DRIVE.MOVE')"
                      @click="moveTarget = file"
                    >
                      <span class="i-lucide-folder-input w-4 h-4" />
                    </button>
                    <button
                      class="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-2 transition-all"
                      :title="t('WORKSPACE_DRIVE.TRASH_ACTION')"
                      @click="trashFile(file)"
                    >
                      <span class="i-lucide-trash-2 w-4 h-4" />
                    </button>
                  </template>
                  <template v-else>
                    <button
                      class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-all"
                      :title="t('WORKSPACE_DRIVE.RESTORE')"
                      @click="restoreFile(file)"
                    >
                      <span class="i-lucide-rotate-ccw w-4 h-4" />
                    </button>
                    <button
                      class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-2 transition-all"
                      :title="t('WORKSPACE_DRIVE.DELETE_FOREVER')"
                      @click="deleteForever(file)"
                    >
                      <span class="i-lucide-trash-2 w-4 h-4" />
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Teleport: position:fixed quebra sob ancestral com transform — os
         modais renderizam direto no <body> (caso do preview, 27/08). -->
    <Teleport to="body">
      <!-- Modal: nome -->
      <div
        v-if="nameModal"
        class="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
        @click.self="nameModal = null"
      >
        <div
          class="w-96 max-w-[90vw] p-5 rounded-2xl bg-n-solid-1 border border-n-weak shadow-xl space-y-4"
        >
          <h3 class="text-base font-semibold text-n-slate-12">
            {{
              nameModal.kind === 'new_folder'
                ? t('WORKSPACE_DRIVE.NEW_FOLDER')
                : t('WORKSPACE_DRIVE.RENAME')
            }}
          </h3>
          <input
            v-model="nameModal.value"
            class="w-full px-3 py-2 text-sm rounded-xl bg-n-solid-2 border border-n-weak text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="t('WORKSPACE_DRIVE.NAME_PLACEHOLDER')"
            @keyup.enter="confirmNameModal"
          />
          <div class="flex justify-end gap-2">
            <button
              class="px-4 py-2 text-sm rounded-xl text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="nameModal = null"
            >
              {{ t('WORKSPACE_DRIVE.CANCEL') }}
            </button>
            <button
              class="px-4 py-2 text-sm font-semibold rounded-xl bg-n-brand text-white hover:bg-n-brand/90 transition-colors"
              @click="confirmNameModal"
            >
              {{ t('WORKSPACE_DRIVE.CONFIRM') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Modal: mover -->
      <div
        v-if="moveTarget"
        class="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
        @click.self="moveTarget = null"
      >
        <div
          class="w-96 max-w-[90vw] p-5 rounded-2xl bg-n-solid-1 border border-n-weak shadow-xl space-y-3"
        >
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('WORKSPACE_DRIVE.MOVE_TO', { name: moveTarget.name }) }}
          </h3>
          <div class="max-h-64 overflow-y-auto space-y-1">
            <button
              class="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-xl text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="moveFile(null)"
            >
              <span class="i-lucide-hard-drive w-4 h-4 text-n-slate-10" />
              {{ t('WORKSPACE_DRIVE.ROOT') }}
            </button>
            <button
              v-for="folder in allFolders"
              :key="`move-${folder.id}`"
              class="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-xl text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-40 transition-colors"
              :disabled="folder.id === moveTarget.folder_id"
              @click="moveFile(folder.id)"
            >
              <span class="i-lucide-folder w-4 h-4 text-n-amber-10" />
              <span class="truncate">{{ folder.name }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Modal: compartilhar -->
      <div
        v-if="shareTarget"
        class="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
        @click.self="shareTarget = null"
      >
        <div
          class="w-[28rem] max-w-[90vw] p-5 rounded-2xl bg-n-solid-1 border border-n-weak shadow-xl space-y-4"
        >
          <h3 class="text-base font-semibold text-n-slate-12 truncate">
            {{ t('WORKSPACE_DRIVE.SHARE_TITLE', { name: shareTarget.name }) }}
          </h3>
          <!-- Escolha do modo (pedido Ivan): PRIVADO ou PÚBLICO, sempre com link -->
          <div class="grid grid-cols-2 gap-2">
            <button
              class="flex flex-col items-start gap-1 p-3 rounded-xl border text-left transition-colors"
              :class="
                !shareTarget.shared
                  ? 'border-n-brand bg-n-brand/10'
                  : 'border-n-weak bg-n-solid-2 hover:bg-n-alpha-1'
              "
              :disabled="isSharing"
              @click="selectShareMode('private')"
            >
              <span
                class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
              >
                <span class="i-lucide-lock w-3.5 h-3.5" />
                {{ t('WORKSPACE_DRIVE.SHARE_PRIVATE') }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ t('WORKSPACE_DRIVE.SHARE_PRIVATE_HINT') }}
              </span>
            </button>
            <button
              class="flex flex-col items-start gap-1 p-3 rounded-xl border text-left transition-colors"
              :class="
                shareTarget.shared
                  ? 'border-n-brand bg-n-brand/10'
                  : 'border-n-weak bg-n-solid-2 hover:bg-n-alpha-1'
              "
              :disabled="isSharing"
              @click="selectShareMode('public')"
            >
              <span
                class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
              >
                <span class="i-lucide-globe w-3.5 h-3.5" />
                {{ t('WORKSPACE_DRIVE.SHARE_PUBLIC_MODE') }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{
                  shareKind === 'folder'
                    ? t('WORKSPACE_DRIVE.SHARE_PUBLIC_FOLDER_HINT')
                    : t('WORKSPACE_DRIVE.SHARE_PUBLIC_HINT')
                }}
              </span>
            </button>
          </div>
          <div class="flex items-center gap-2">
            <input
              :value="currentShareLink"
              readonly
              class="grow px-3 py-2 text-xs rounded-xl bg-n-solid-2 border border-n-weak text-n-slate-11 outline-none"
              @focus="$event.target.select()"
            />
            <button
              class="shrink-0 px-3 py-2 text-xs font-semibold rounded-xl bg-n-solid-3 text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="copyShareLink"
            >
              {{
                shareCopied
                  ? t('WORKSPACE_DRIVE.SHARE_COPIED')
                  : t('WORKSPACE_DRIVE.SHARE_COPY')
              }}
            </button>
          </div>
        </div>
      </div>

      <!-- Modal: mover PASTA -->
      <div
        v-if="moveFolderTarget"
        class="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
        @click.self="moveFolderTarget = null"
      >
        <div
          class="w-96 max-w-[90vw] p-5 rounded-2xl bg-n-solid-1 border border-n-weak shadow-xl space-y-3"
        >
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('WORKSPACE_DRIVE.MOVE_TO', { name: moveFolderTarget.name }) }}
          </h3>
          <div class="max-h-64 overflow-y-auto space-y-1">
            <button
              class="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-xl text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="moveFolder(null)"
            >
              <span class="i-lucide-hard-drive w-4 h-4 text-n-slate-10" />
              {{ t('WORKSPACE_DRIVE.ROOT') }}
            </button>
            <button
              v-for="folder in allFolders.filter(
                f => f.id !== moveFolderTarget.id
              )"
              :key="`movepasta-${folder.id}`"
              class="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-xl text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-40 transition-colors"
              :disabled="folder.id === moveFolderTarget.parent_id"
              @click="moveFolder(folder.id)"
            >
              <span class="i-lucide-folder w-4 h-4 text-n-amber-10" />
              <span class="truncate">{{ folder.name }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Modal: preview (tela cheia, estilo Google Drive) -->
      <div
        v-if="preview"
        class="fixed inset-0 z-50 flex flex-col bg-black/95"
        @click.self="preview = null"
      >
        <!-- Barra superior -->
        <div class="flex h-14 shrink-0 items-center gap-3 px-4 text-white">
          <button
            class="p-2 rounded-full hover:bg-white/10 transition-colors"
            :title="t('WORKSPACE_DRIVE.CLOSE')"
            @click="preview = null"
          >
            <span class="i-lucide-x w-5 h-5" />
          </button>
          <span
            :class="iconFor(preview.content_type)"
            class="w-5 h-5 shrink-0"
          />
          <p class="min-w-0 flex-1 truncate text-sm font-medium">
            {{ preview.name }}
          </p>
          <span class="hidden sm:block text-xs text-white/60 tabular-nums">
            {{
              `${formatSize(preview.byte_size)} · ${formatDate(preview.created_at)}`
            }}
          </span>
          <button
            class="flex items-center gap-2 px-3 py-1.5 text-sm rounded-full hover:bg-white/10 transition-colors"
            @click="openShare(preview)"
          >
            <span class="i-lucide-link w-4 h-4" />
            {{ t('WORKSPACE_DRIVE.SHARE') }}
          </button>
          <a
            v-if="preview.url"
            :href="preview.download_url || preview.url"
            download
            class="flex items-center gap-2 px-3 py-1.5 text-sm rounded-full hover:bg-white/10 transition-colors"
          >
            <span class="i-lucide-download w-4 h-4" />
            {{ t('WORKSPACE_DRIVE.DOWNLOAD') }}
          </a>
        </div>

        <!-- Mídia -->
        <div
          class="relative flex min-h-0 flex-1 items-center justify-center px-16 pb-6"
          @click.self="preview = null"
        >
          <button
            v-if="filteredFiles.length > 1"
            class="absolute left-4 top-1/2 -translate-y-1/2 flex size-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            @click.stop="navPreview(-1)"
          >
            <span class="i-lucide-chevron-left w-6 h-6" />
          </button>
          <img
            v-if="previewKind(preview) === 'image'"
            :src="preview.url"
            :alt="preview.name"
            class="max-h-full max-w-full rounded-lg object-contain"
          />
          <video
            v-else-if="previewKind(preview) === 'video'"
            :key="preview.id"
            :src="preview.url"
            controls
            autoplay
            class="max-h-full max-w-full rounded-lg"
          />
          <audio
            v-else-if="previewKind(preview) === 'audio'"
            :key="preview.id"
            :src="preview.url"
            controls
            autoplay
            class="w-full max-w-xl"
          />
          <iframe
            v-else-if="previewKind(preview) === 'pdf'"
            :src="preview.url"
            class="h-full w-full max-w-5xl rounded-lg bg-white"
            :title="preview.name"
          />
          <div
            v-else
            class="flex flex-col items-center gap-3 py-16 text-white/80"
          >
            <span :class="iconFor(preview.content_type)" class="w-12 h-12" />
            <p class="text-sm">{{ t('WORKSPACE_DRIVE.NO_PREVIEW') }}</p>
          </div>
          <button
            v-if="filteredFiles.length > 1"
            class="absolute right-4 top-1/2 -translate-y-1/2 flex size-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            @click.stop="navPreview(1)"
          >
            <span class="i-lucide-chevron-right w-6 h-6" />
          </button>
        </div>
      </div>

      <!-- Menu do arquivo (flutuante, imune a overflow/stacking) -->
      <div
        v-if="menuFile"
        class="fixed w-48 rounded-xl border border-n-weak bg-n-solid-1 shadow-xl z-[60] py-1"
        :style="{ left: `${menuPos.x}px`, top: `${menuPos.y}px` }"
        @click.stop
      >
        <template v-if="section !== 'trash'">
          <a
            v-if="menuFile.url"
            :href="menuFile.download_url || menuFile.url"
            download
            class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
          >
            <span class="i-lucide-download w-4 h-4 text-n-slate-10" />
            {{ t('WORKSPACE_DRIVE.DOWNLOAD') }}
          </a>
          <button
            class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
            @click="openShare(menuFile)"
          >
            <span
              class="i-lucide-link w-4 h-4"
              :class="menuFile.shared ? 'text-n-teal-10' : 'text-n-slate-10'"
            />
            {{ t('WORKSPACE_DRIVE.SHARE') }}
          </button>
          <button
            class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
            @click="copyFile(menuFile)"
          >
            <span class="i-lucide-copy w-4 h-4 text-n-slate-10" />
            {{ t('WORKSPACE_DRIVE.MAKE_COPY') }}
          </button>
          <button
            class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
            @click="toggleStar(menuFile)"
          >
            <span class="i-lucide-star w-4 h-4 text-n-slate-10" />
            {{
              menuFile.starred
                ? t('WORKSPACE_DRIVE.UNSTAR')
                : t('WORKSPACE_DRIVE.STAR')
            }}
          </button>
          <button
            class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
            @click="openNameModal('rename_file', menuFile.id, menuFile.name)"
          >
            <span class="i-lucide-pencil w-4 h-4 text-n-slate-10" />
            {{ t('WORKSPACE_DRIVE.RENAME') }}
          </button>
          <button
            class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
            @click="
              moveTarget = file;
              menuFor = null;
            "
          >
            <span class="i-lucide-folder-input w-4 h-4 text-n-slate-10" />
            {{ t('WORKSPACE_DRIVE.MOVE') }}
          </button>
          <button
            class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-ruby-11 hover:bg-n-alpha-2"
            @click="trashFile(menuFile)"
          >
            <span class="i-lucide-trash-2 w-4 h-4" />
            {{ t('WORKSPACE_DRIVE.TRASH_ACTION') }}
          </button>
        </template>
        <template v-else>
          <button
            class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
            @click="restoreFile(menuFile)"
          >
            <span class="i-lucide-rotate-ccw w-4 h-4 text-n-slate-10" />
            {{ t('WORKSPACE_DRIVE.RESTORE') }}
          </button>
          <button
            class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-ruby-11 hover:bg-n-alpha-2"
            @click="deleteForever(menuFile)"
          >
            <span class="i-lucide-trash-2 w-4 h-4" />
            {{ t('WORKSPACE_DRIVE.DELETE_FOREVER') }}
          </button>
        </template>
      </div>
    </Teleport>
  </div>
</template>
